import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Tooltip } from 'primereact/tooltip';
import { Skeleton } from 'primereact/skeleton';
import { ShippingOrder, ShippingLineItem } from '../../DeShippingTableWrapper';
import { LineItemStatusBadge } from '../LineItemStatusBadge';
import { useLineItems } from '../../hooks/useLineItems';
import { useLineItemImages } from '../../hooks/useLineItemImages';
import { LineItemFulfillmentLocationTemplate } from '../shared/LineItemFulfillmentLocationTemplate';

interface LineItemsTabProps {
  lineItemsHook: ReturnType<typeof useLineItems>;
  imageHook: ReturnType<typeof useLineItemImages>;
}

export const LineItemsTab: React.FC<LineItemsTabProps> = ({ imageHook, lineItemsHook }) => {
  const { lineItems, loading, error, selectedLineItem, setSelectedLineItem } = lineItemsHook;

  const statusBodyTemplate = (rowData: ShippingLineItem) => {
    return (
      <LineItemStatusBadge status={rowData.fulfillment_status || 'unassigned'} />
    );
  };


  const imagesBodyTemplate = (rowData: ShippingLineItem) => {
    return (
      <Button
        label="View Images"
        icon="pi pi-images"
        size="small"
        outlined
        onClick={() => {
          setSelectedLineItem(rowData);
          imageHook.openImageViewer(rowData.workflow_id);
        }}
      />
    );
  };

  const priceBodyTemplate = (rowData: ShippingLineItem) => {
    return `$${rowData.product_price?.toFixed(2) || '0.00'}`;
  };

  // Skeleton templates for loading state
  const skeletonBodyTemplate = () => <Skeleton height="1.5rem" />;
  const skeletonShortTemplate = () => <Skeleton height="1.5rem" width="4rem" />;
  const skeletonBadgeTemplate = () => <Skeleton height="1.5rem" width="5rem" borderRadius="16px" />;
  const skeletonButtonTemplate = () => <Skeleton height="2rem" width="7rem" borderRadius="4px" />;

  const header = (
    <div className="flex justify-content-end">
      <div className="text-sm text-600">
        {loading ? <Skeleton height="1rem" width="8rem" /> : `Showing ${lineItems.length} items`}
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="p-4">
        <Message severity="error" text={error} />
      </div>
    );
  }

  // Create skeleton data for loading state
  const skeletonData = loading ? [{}] : lineItems;

  return (
    <div className="line-items-tab">
      <DataTable
        value={skeletonData}
        header={header}
        paginator={!loading}
        rows={10}
        rowsPerPageOptions={[5, 10, 25]}
        tableStyle={{ minWidth: '50rem' }}
        emptyMessage="No line items found"
      >
        <Column 
          field="order_name" 
          header="Order" 
          sortable={!loading}
          body={loading ? skeletonBodyTemplate : undefined}
        />
        <Column 
          field="product_name" 
          header="Product" 
          sortable={!loading}
          body={loading ? skeletonBodyTemplate : undefined}
        />
        <Column 
          field="product_sku" 
          header="SKU" 
          sortable={!loading}
          body={loading ? skeletonShortTemplate : undefined}
        />
        <Column 
          field="variant_title" 
          header="Variant"
          body={loading ? skeletonBodyTemplate : undefined}
        />
        <Column 
          field="product_quantity" 
          header="Qty" 
          sortable={!loading}
          body={loading ? skeletonShortTemplate : undefined}
        />
        <Column 
          field="product_price" 
          header="Price" 
          body={loading ? skeletonShortTemplate : priceBodyTemplate} 
          sortable={!loading}
        />
        <Column 
          field="fulfillment_status" 
          header="Status" 
          body={loading ? skeletonBadgeTemplate : statusBodyTemplate} 
          sortable={!loading}
        />
        <Column 
          field="fulfillment_location" 
          header="Location" 
          body={loading ? skeletonBodyTemplate : LineItemFulfillmentLocationTemplate} 
          sortable={!loading}
        />
        <Column 
          header="Images" 
          body={loading ? skeletonButtonTemplate : imagesBodyTemplate} 
        />
      </DataTable>
    </div>
  );
};